// src/lib/permissions/core/engine.ts
// Clean, centralized permission engine with no legacy dependencies

import { PermissionMap } from '../../../types/access';
import { PermissionModule, ModuleCategory, AccessLevel } from './modules';
import { PermissionCache } from './cache';

export interface PermissionStats {
  totalPermissions: number;
  grantedPermissions: number;
  accessibleModules: number;
  adminAccess: boolean;
  moduleStats: Record<string, AccessLevel>;
}

export interface ValidationResult {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
}

/**
 * Core Permission Engine
 * Single source of truth for all permission logic
 */
export class PermissionEngine {
  private permissions: PermissionMap;
  private modules: Map<string, PermissionModule>;
  private cache: PermissionCache;

  constructor(permissions: PermissionMap, modules: PermissionModule[]) {
    this.permissions = permissions;
    this.modules = new Map(modules.map(m => [m.name, m]));
    this.cache = new PermissionCache();
  }

  /**
   * Update permissions and invalidate relevant cache
   */
  updatePermissions(permissions: PermissionMap): void {
    this.permissions = permissions;
    this.cache.clear();
  }

  /**
   * Core permission check - fundamental building block
   */
  hasPermission(permissionKey: string): boolean {
    return this.cache.get(`perm:${permissionKey}`, () => {
      return this.permissions[permissionKey] === true;
    });
  }

  /**
   * Check multiple permissions with different strategies
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(p => this.hasPermission(p));
  }

  /**
   * Module access checking
   */
  canAccessModule(moduleName: string): boolean {
    return this.cache.get(`module:${moduleName}`, () => {
      const module = this.modules.get(moduleName);
      if (!module) {
        console.warn(`Module '${moduleName}' not found`);
        return false;
      }
      return module.permissions.some(p => this.hasPermission(p));
    });
  }

  /**
   * View-specific access (read operations)
   */
  canViewModule(moduleName: string): boolean {
    return this.cache.get(`view:${moduleName}`, () => {
      const module = this.modules.get(moduleName);
      if (!module) return false;
      
      const viewPermissions = module.permissions.filter(p => 
        p.includes('_view') || p.includes('view') || p.includes('tile_view')
      );
      
      return viewPermissions.some(p => this.hasPermission(p));
    });
  }

  /**
   * Update-specific access (write operations)
   */
  canUpdateModule(moduleName: string): boolean {
    return this.cache.get(`update:${moduleName}`, () => {
      const module = this.modules.get(moduleName);
      if (!module) return false;
      
      const updatePermissions = module.permissions.filter(p => 
        p.includes('_update') || p.includes('update')
      );
      
      return updatePermissions.some(p => this.hasPermission(p));
    });
  }

  /**
   * Get comprehensive access level for a module
   */
  getModuleAccessLevel(moduleName: string): AccessLevel {
    if (this.canUpdateModule(moduleName)) return 'update';
    if (this.canViewModule(moduleName)) return 'view';
    return 'none';
  }

  /**
   * Admin access detection
   */
  hasAdminAccess(): boolean {
    return this.cache.get('admin:access', () => {
      const adminModules = Array.from(this.modules.values())
        .filter(m => m.category === 'admin');
      
      return adminModules.some(module => this.canAccessModule(module.name));
    });
  }

  /**
   * Get all accessible modules
   */
  getAccessibleModules(): PermissionModule[] {
    return Array.from(this.modules.values())
      .filter(module => this.canAccessModule(module.name));
  }

  /**
   * Get modules by category with access filtering
   */
  getModulesByCategory(category: ModuleCategory): PermissionModule[] {
    return Array.from(this.modules.values())
      .filter(m => m.category === category)
      .filter(module => this.canAccessModule(module.name));
  }

  /**
   * Comprehensive permission statistics
   */
  getPermissionStats(): PermissionStats {
    const totalPermissions = Object.keys(this.permissions).length;
    const grantedPermissions = Object.values(this.permissions)
      .filter(p => p === true).length;
    const accessibleModules = this.getAccessibleModules().length;
    const adminAccess = this.hasAdminAccess();

    const moduleStats: Record<string, AccessLevel> = {};
    Array.from(this.modules.keys()).forEach(moduleName => {
      moduleStats[moduleName] = this.getModuleAccessLevel(moduleName);
    });

    return {
      totalPermissions,
      grantedPermissions,
      accessibleModules,
      adminAccess,
      moduleStats
    };
  }

  /**
   * Permission validation and consistency checking
   */
  validatePermissions(): ValidationResult {
    const issues: string[] = [];
    const suggestions: string[] = [];

    // Check for orphaned permissions
    const allModulePermissions = new Set(
      Array.from(this.modules.values()).flatMap(m => m.permissions)
    );

    Object.keys(this.permissions).forEach(permission => {
      if (!allModulePermissions.has(permission)) {
        issues.push(`Orphaned permission: ${permission}`);
        suggestions.push(`Remove unused permission: ${permission}`);
      }
    });

    // Check for inconsistent access patterns
    Array.from(this.modules.values()).forEach(module => {
      const viewPerms = module.permissions.filter(p => p.includes('view'));
      const updatePerms = module.permissions.filter(p => p.includes('update'));

      updatePerms.forEach(updatePerm => {
        if (this.hasPermission(updatePerm)) {
          const basePermName = updatePerm.replace('_update', '_view');
          if (viewPerms.includes(basePermName) && !this.hasPermission(basePermName)) {
            issues.push(`Has update but missing view permission for ${module.name}`);
            suggestions.push(`Grant view permission: ${basePermName}`);
          }
        }
      });
    });

    return {
      isValid: issues.length === 0,
      issues,
      suggestions
    };
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats() {
    return this.cache.getStats();
  }
}